import { MultiStyleText } from "slotworks/graphics/pixi/pixi-multistyle-text";
import { SoundEvent } from "slotworks/sound/sound-events";
import SoundManager from "slotworks/sound/sound-manager";

export default class ButtonElement {

    public static DISABLED_TINT: number = 0xF1F1F1;
    public static OVER_TINT: number = 0x643c64;
    public static DOWN_SCALE: number = 0.8;

    public container: PIXI.Container;
    public downScale: number = ButtonElement.DOWN_SCALE;
    public overTint: number = ButtonElement.OVER_TINT;
    public disabledTint: number = ButtonElement.DISABLED_TINT;

    private name: string;

    private states: Map<ButtonState, PIXI.Sprite>;
    private labels: Map<ButtonState, PIXI.extras.BitmapText | PIXI.Text>;
    private currentDisplay: PIXI.Sprite;
    private currentState: ButtonState = ButtonState.Up;
    private enabled: boolean;
    private selected: boolean;

    constructor(container: PIXI.Container) {
        this.container = container;
        this.states = new Map<ButtonState, PIXI.Sprite>();
        this.labels = new Map<ButtonState, PIXI.extras.BitmapText | PIXI.Text>();
        this.enabled = true;
        this.selected = false;

        this.name = container.name.split("btn-").join("");

        this.container.interactive = true;
        this.container.buttonMode = true;

        this.container.on(ButtonEvent.POINTER_UP.getEventString(), () => {
            this.changeState(ButtonState.Up);
        });
        this.container.on(ButtonEvent.POINTER_UP_OUTSIDE.getEventString(), () => {
            this.changeState(ButtonState.Up);
        });
        this.container.on(ButtonEvent.POINTER_OUT.getEventString(), () => {
            this.changeState(ButtonState.Up);
        });
        this.container.on(ButtonEvent.POINTER_OVER.getEventString(), () => {
            this.changeState(ButtonState.Over);
        });
        this.container.on(ButtonEvent.POINTER_DOWN.getEventString(), () => {
            this.changeState(ButtonState.Down);
        });
        this.container.on(ButtonEvent.POINTER_DOWN.getEventString(), this.playSound);
    }

    public on(event: ButtonEvent, callback: Function) {
        this.container.on(event.getEventString(), callback);
    }

    public off(event: ButtonEvent, callback: Function) {
        this.container.off(event.getEventString(), callback);
    }

    public addState(state: ButtonState | string, target: PIXI.Sprite) {

        let buttonState: ButtonState;

        if (typeof state === "string") {
            buttonState = ButtonState.FromString(state);
        } else {
            buttonState = state;
        }

        if (!this.container.hitArea) {
            this.setHitArea(target);
        }

        this.states.set(buttonState, target);

        target.visible = false;

        target.anchor.set(0.5, 0.5);
        target.x += target.width * 0.5;
        target.y += target.height * 0.5;

        this.container.addChild(target);

        this.update();
    }

    public addLabelForState(label: PIXI.extras.BitmapText | PIXI.Text, state: ButtonState) {
        this.container.addChild(label);
        label.x -= this.container.x;
        label.y -= this.container.y;
        label.visible = false;

        this.labels.set(state, label);
        this.updateLabels();
    }

    public setHitArea(target: PIXI.Container) {
        this.container.hitArea = target.getBounds();
    }

    public setEnabled(enabled: boolean) {
        this.container.interactive = enabled;
        this.enabled = enabled;

        if (this.enabled) {
            this.currentState = ButtonState.Up;
        }

        this.update();
    }

    public setSelected(selected: boolean) {
        this.selected = selected;

        if (selected) {
            this.currentState = ButtonState.Selected;
        } else {
            this.currentState = ButtonState.Up;
        }

        this.update();

        this.setEnabled(!selected);
    }

    public setVisible(enabled: boolean) {
        this.container.visible = enabled;

        this.setLabelsVisibility(enabled);
    }

    public setAlpha(alpha: number) {
        this.container.alpha = alpha;
    }

    public setPosition(x: number, y: number) {
        this.container.x = x;
        this.container.y = y;
    }

    public destroy() {
        this.currentDisplay = null;
        this.states = null;

        this.container.off(ButtonEvent.POINTER_DOWN.getEventString(), this.playSound);

        while (this.container.children.length) {
            this.container.removeChildAt(0);
        }

        this.container.parent.removeChild(this.container);
    }

    // TODO: Add label support
    public clone(newID?: string) {
        const container = new PIXI.Container();
        container.name = "btn-" + this.name;
        container.x = this.container.x;
        container.y = this.container.y;
        const toReturn = new ButtonElement(container);

        this.states.forEach((value, key) => {
            toReturn.addState(key, new PIXI.Sprite(value.texture));
        });

        toReturn.container.hitArea = this.container.hitArea;

        if (newID) {
            toReturn.name = newID;
        } else {
            toReturn.name = this.name;
        }

        toReturn.enabled = this.enabled;
        toReturn.selected = this.selected;

        return toReturn;
    }

    private update() {
        if (!this.enabled) {
            if (!this.selected) {
                this.currentState = ButtonState.Disabled;
            }

            this.container.buttonMode = false;
        } else {
            this.container.buttonMode = true;
        }

        if (this.states && this.states.has(ButtonState.All)) {
            this.currentDisplay = this.states.get(ButtonState.All);
            this.currentDisplay.visible = true;
        } else {
            this.updateMultistateButton();
        }

        this.updateLabels();
    }

    private updateMultistateButton() {
        this.resetCurrentDisplay();

        let nextDisplay: PIXI.Sprite;

        if (this.states) {
            if (this.states.has(this.currentState)) {
                nextDisplay = this.states.get(this.currentState);
            } else if (this.currentState === ButtonState.Disabled) {
                nextDisplay = this.states.get(ButtonState.Up);
                nextDisplay.tint = this.disabledTint;
            } else if (this.currentState === ButtonState.Over) {
                nextDisplay = this.states.get(ButtonState.Up);
                nextDisplay.tint = this.overTint;
            } else if (this.currentState === ButtonState.Down) {
                nextDisplay = this.states.get(ButtonState.Up);
                nextDisplay.scale.x = nextDisplay.scale.y = this.downScale;
            } else if (this.states.has(ButtonState.Up)) {
                nextDisplay = this.states.get(ButtonState.Up);
            }
        }

        if (nextDisplay) {
            nextDisplay.visible = true;
        }
        if (this.currentDisplay && this.currentDisplay !== nextDisplay) {
            this.currentDisplay.visible = false;
        }
        this.currentDisplay = nextDisplay;
    }

    private resetCurrentDisplay() {
        if (this.currentDisplay) {
            this.currentDisplay.tint = 0xFFFFFF;
            this.currentDisplay.scale.x = this.currentDisplay.scale.y = 1;
        }
    }

    private updateLabels() {
        if (this.labels.has(this.currentState)) {
            this.labels.forEach((label: MultiStyleText, state: ButtonState) => {
                if (state === this.currentState) {
                    label.visible = true;
                } else {
                    label.visible = false;
                }
            });
        }
    }

    private setLabelsVisibility(visible: boolean) {
        if (!visible) {
            this.labels.forEach((label: MultiStyleText, state: ButtonState) => {
                label.visible = false;
            });
        } else {
            this.updateLabels();
        }
    }

    private changeState(state: ButtonState) {
        this.currentState = state;
        this.update();
    }

    private playSound = () => {
        SoundManager.event(SoundEvent.button_press_NAME, this.name);
    }
}

export class ButtonState {
    public static Up: ButtonState = new ButtonState("up");
    public static Over: ButtonState = new ButtonState("over");
    public static Down: ButtonState = new ButtonState("down");
    public static Disabled: ButtonState = new ButtonState("disabled");
    public static Selected: ButtonState = new ButtonState("selected");
    public static All: ButtonState = new ButtonState("all");

    public static FromString(str: string): ButtonState {
        return ButtonState.buttonStates.get(str);
    }

    private static buttonStates: Map<string, ButtonState>;

    public label: string;

    constructor(label: string) {
        this.label = label;

        if (!ButtonState.buttonStates) {
            ButtonState.buttonStates = new Map<string, ButtonState>();
        }

        ButtonState.buttonStates.set(label, this);
    }
}

export class ButtonEvent {

    public static POINTER_UP: ButtonEvent = new ButtonEvent("pointerup");
    public static POINTER_UP_OUTSIDE: ButtonEvent = new ButtonEvent("pointerupoutside");
    public static POINTER_OUT: ButtonEvent = new ButtonEvent("pointerout");
    public static POINTER_OVER: ButtonEvent = new ButtonEvent("pointerover");
    public static POINTER_DOWN: ButtonEvent = new ButtonEvent("pointerdown");
    public static POINTER_MOVE: ButtonEvent = new ButtonEvent("pointermove");

    private id: string;

    constructor(id: string) {
        this.id = id;
    }

    public getEventString(): string {
        if (!(window as any).PointerEvent) {

            if ("ontouchstart" in window) {
                if (this.id === "pointerdown") {
                    return "touchstart";
                }
                if (this.id === "pointerup") {
                    return "touchend";
                }
                if (this.id === "pointerupoutside") {
                    return "touchcancel";
                }
                if (this.id === "pointermove") {
                    return "touchmove";
                }
            } else {
                if (this.id === "pointerdown") {
                    return "mousedown";
                }
                if (this.id === "pointerup") {
                    return "mouseup";
                }
                if (this.id === "pointermove") {
                    return "mousemove";
                }
            }
        }

        return this.id;
    }
}



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./~/slotworks/graphics/elements/button-element.ts